import { Field, Segmented, Select } from '@pa/ui'
import { AlertTriangle, Plus, X } from 'lucide-react'
import { useId, useState } from 'react'
import type { Settings } from '../model/types'
import { useSettingsDraft } from '../SettingsDraftProvider'

type Rules = Settings['rules']

/** C-09's read-only notice while a super admin has the clinic's testing switch on (`testingIgnorePfHours`). */
export const TESTING_NOTICE = {
  title: 'Testing only: Practice Fusion facility hours are ignored',
  body: "A super admin turned this on for testing. Patients can book any time you're open in Clinic profile, even when Practice Fusion is closed (for example on weekends). Ask your super admin to turn it off after testing.",
}

/**
 * C-09 Booking rules (UserFlows C-09): default length, coverage (from PF), how far ahead, minimum notice and the
 * reasons-for-visit chips (Add or Enter; blanks and case-insensitive duplicates are ignored). While a super admin has
 * the testing switch on, a read-only notice says so (clinic users can't change it).
 */
export function RulesTab() {
  const { draft, update } = useSettingsDraft()
  const [reason, setReason] = useState('')
  const ids = { coverage: useId(), reasons: useId() }
  const set = (p: Partial<Rules>) => {
    update((d) => ({ ...d, rules: { ...d.rules, ...p } }))
  }
  const addReason = () => {
    const r = reason.trim()
    const taken = draft.rules.visitReasons.some((x) => x.toLowerCase() === r.toLowerCase())
    if (!r || taken) return
    set({ visitReasons: [...draft.rules.visitReasons, r] })
    setReason('')
  }
  // Before the first sync PF hasn't told us its coverage types: offer the saved code only (task 04 assumption).
  const coverage = draft.coverageOptions.some((c) => c.code === draft.rules.coverageCode)
    ? draft.coverageOptions
    : [{ code: draft.rules.coverageCode, name: draft.rules.coverageCode }, ...draft.coverageOptions]
  return (
    <div className="card card-pad set-form">
      {draft.testingIgnorePfHours && (
        <div className="warn-box" role="note" aria-label="Testing only">
          <AlertTriangle aria-hidden />
          <div>
            <p>
              <b>{TESTING_NOTICE.title}</b>
            </p>
            <p>{TESTING_NOTICE.body}</p>
          </div>
        </div>
      )}
      <Field
        label="Default appointment length"
        hint="Used to build open times for visit types without their own length. Not shown to patients."
      >
        <Segmented
          label="Default appointment length"
          value={draft.rules.defaultDurationMinutes}
          onChange={(v) => {
            set({ defaultDurationMinutes: v })
          }}
          options={([15, 20, 30, 45, 60] as const).map((m) => ({ value: m, label: `${String(m)} min` }))}
        />
      </Field>
      <Field label="Coverage" hint="Written to every appointment in PF. Patients aren't asked." labelId={ids.coverage}>
        <div className="rules-select">
          <Select
            labelledBy={ids.coverage}
            value={draft.rules.coverageCode}
            onChange={(v) => {
              set({ coverageCode: v })
            }}
            options={coverage.map((c) => ({ value: c.code, label: c.name }))}
          />
        </div>
      </Field>
      <Field label="How far ahead patients can book">
        <Segmented
          label="How far ahead patients can book"
          value={draft.rules.bookingWindowDays}
          onChange={(v) => {
            set({ bookingWindowDays: v })
          }}
          options={([14, 30, 45, 60, 90] as const).map((n) => ({ value: n, label: `${String(n)} days` }))}
        />
      </Field>
      <Field label="Minimum notice" hint="The earliest time a patient can book, counted from now.">
        <Segmented
          label="Minimum notice"
          value={draft.rules.minNoticeHours}
          onChange={(v) => {
            set({ minNoticeHours: v })
          }}
          options={([0, 1, 2, 4, 24, 48] as const).map((n) => ({
            value: n,
            label: n === 0 ? 'None' : `${String(n)} h`,
          }))}
        />
      </Field>
      <Field
        label="Reasons for visit"
        hint={'Quick picks patients see under "Reason for visit". They can always write their own.'}
        htmlFor={ids.reasons}
      >
        <div className="chip-list reasons-list">
          {draft.rules.visitReasons.map((r) => (
            <span key={r} className="mini-chip">
              {r}
              <button
                type="button"
                aria-label={`Remove ${r}`}
                onClick={() => {
                  set({ visitReasons: draft.rules.visitReasons.filter((x) => x !== r) })
                }}
              >
                <X aria-hidden />
              </button>
            </span>
          ))}
        </div>
        <div className="row reason-add">
          <input
            id={ids.reasons}
            className="input"
            placeholder="Add a reason"
            maxLength={80}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addReason()
              }
            }}
          />
          <button type="button" className="btn btn-secondary" onClick={addReason} disabled={!reason.trim()}>
            <Plus aria-hidden /> Add
          </button>
        </div>
      </Field>
    </div>
  )
}
